import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import Link from "next/link";
import StarRating from "../components/StarRating";

const kosherStyles = `
.tr-kosher__title {
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
  margin: 56px 0 32px;
  padding: 0 16px;
}

/* ── Filtres catégories ── */
.tr-kosher__cats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 48px;
  padding: 0 16px;
}
.tr-kosher__cat {
  padding: 10px 22px;
  border-radius: 999px;
  border: none;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white);
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, color .15s, transform .15s;
}
.tr-kosher__cat:hover {
  background: color-mix(in srgb, var(--primary, #1b579e) 20%, white);
}
.tr-kosher__cat--active {
  background: var(--primary, #1b579e);
  color: white;
}

/* ── Section par catégorie ── */
.tr-kosher__section {
  margin-bottom: 56px;
}
.tr-kosher__section-title {
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0 0 28px;
}

/* ── Grille ── */
.tr-kosher__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 360px));
  gap: 24px;
  justify-content: center;
  max-width: 1180px;
  margin: 0 auto;
}
@media (max-width: 640px) {
  .tr-kosher__grid { grid-template-columns: minmax(0, 400px); }
}

/* ── Carte ── */
.tr-kcard {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  transition: transform .25s ease, box-shadow .25s ease;
  background: #1a1a2e;
  text-decoration: none;
  color: inherit;
  display: block;
}
.tr-kcard:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06);
}
.tr-kcard__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-kcard:hover .tr-kcard__img { transform: scale(1.06); }
.tr-kcard__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: #4b5563;
  font-size: 40px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}
.tr-kcard__wash {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(9,18,26,0) 30%, rgba(9,18,26,.55) 60%, rgba(9,18,26,.92) 100%);
}
.tr-kcard__body {
  position: absolute; inset: auto 0 0 0;
  padding: 18px 20px 20px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tr-kcard__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.tr-kcard__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: .02em;
}
.tr-kcard__badge--category {
  background: var(--primary, #1b579e);
  color: white;
}
/* Couleurs alignées sur la convention culinaire (cf. ui-components/_shared.js) */
.tr-kcard__badge--meat   { background: #dc2626; color: white; }
.tr-kcard__badge--dairy  { background: #3b82f6; color: white; }
.tr-kcard__badge--parve  { background: #111827; color: white; border: 1px solid rgba(255,255,255,.3); }
.tr-kcard__badge--fish   { background: #06b6d4; color: white; }
.tr-kcard__name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: white;
}
.tr-kcard__address {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(255,255,255,.88);
  line-height: 1.35;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,.15);
}
.tr-kcard__address-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Empty state ── */
.tr-kosher__empty {
  text-align: center;
  padding: 64px 16px;
  color: #6b7280;
  font-size: 15px;
}
`;

function KosherCard({ context, place, category, lang, }) {
    const localized = applyLang(place, lang);
    const localizedCat = category ? applyLang(category, lang) : null;
    const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
    const address = place.address?.address;

    const MEAT_TYPE_LABELS = {
        meat: t`Viande`,
        dairy: t`Lacté`,
        parve: t`Parvé`,
    };

    return (
        <Link href={context.org.BASE_WEB_URL+"/travel/kosher-places/"+place.id} legacyBehavior>
            <a className="tr-kcard">
                {imgSrc ? (
                    <img src={imgSrc} alt={localized.name} className="tr-kcard__img" loading="lazy" />
                ) : (
                    <div className="tr-kcard__img-placeholder">
                        {renderIcon({ name: "IconBuildingStore", source: "tabler" }, 48)}
                    </div>
                )}
                <div className="tr-kcard__wash" />
                <div className="tr-kcard__body">
                    <div className="tr-kcard__badges">
                        {localizedCat?.label && (
                            <span className="tr-kcard__badge tr-kcard__badge--category">
                            {localizedCat.label}
                        </span>
                        )}
                        {(place.meatType || []).map(mt => (
                            <span key={mt} className={`tr-kcard__badge tr-kcard__badge--${mt}`}>
                            {MEAT_TYPE_LABELS[mt] || mt}
                        </span>
                        ))}
                        <StarRating value={place.note} size={14} />
                    </div>
                    <div className="tr-kcard__name">{localized.name}</div>
                    {address && (
                        <div className="tr-kcard__address">
                        <span className="tr-kcard__address-icon">
                            {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                        </span>
                            <span>{address}</span>
                        </div>
                    )}
                </div>
            </a>
        </Link>
    );
}

export default function KosherPlaces({ context, loading }) {
    const { org, config, travel } = context;

    const categories = (travel?.kosher?.categories || []).map(c => applyLang(c, context.lang));
    const allPlaces = (travel?.kosherPlaces || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

    const usedCatIds = new Set(allPlaces.map(p => p.categoryId).filter(Boolean));
    const activeCategories = categories.filter(c => usedCatIds.has(c.id));

    const [activeCat, setActiveCat] = React.useState(null);

    const visiblePlaces = activeCat
        ? allPlaces.filter(p => p.categoryId === activeCat)
        : allPlaces;

    const placesByCategory = React.useMemo(() => {
        const map = new Map();
        for (const cat of activeCategories) {
            map.set(cat.id, visiblePlaces.filter(p => p.categoryId === cat.id));
        }
        const orphans = visiblePlaces.filter(p => !p.categoryId || !activeCategories.find(c => c.id === p.categoryId));
        if (orphans.length > 0) map.set("__other__", orphans);
        return map;
    }, [visiblePlaces, activeCategories]);

    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    // JSON-LD ItemList de Restaurant : aide Google à comprendre la liste des lieux cachères.
    const itemListElements = allPlaces.slice(0, 50).map((p, i) => {
        const loc = applyLang(p, context.lang);
        return {
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "Restaurant",
                name: loc.name,
                image: p.image ? (p.image.startsWith("http") ? p.image : `${ASSET_URL}${p.image}`) : undefined,
                address: p.address?.address ? {
                    "@type": "PostalAddress",
                    streetAddress: p.address.address,
                } : undefined,
                geo: (p.address?.lat && p.address?.lon) ? {
                    "@type": "GeoCoordinates",
                    latitude: p.address.lat,
                    longitude: p.address.lon,
                } : undefined,
                servesCuisine: "Kosher",
            },
        };
    });
    const jsonLd = itemListElements.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: t`Restaurants cachères à ${org.ville || org.name}`,
        itemListElement: itemListElements,
    } : null;

    return (
        <div className="travel-page travel-page--kosher tr-kosher">
            <style dangerouslySetInnerHTML={{ __html: kosherStyles }} />
            <Meta
                context={context}
                title={t`Manger Cacher à ${org.ville || org.name}`}
                description={config["meta.description"] || t`Restaurants, traiteurs et lieux cachères à ${org.ville || org.name} — supervision, horaires et contacts`}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-kosher__title">
                        <Trans>Manger Cacher à</Trans>{" "}{org.ville || org.name}
                    </h1>

                    {activeCategories.length > 0 && (
                        <div className="tr-kosher__cats">
                            {activeCategories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`tr-kosher__cat${activeCat === cat.id ? " tr-kosher__cat--active" : ""}`}
                                    onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {allPlaces.length === 0 && (
                        <div className="tr-kosher__empty">
                            <Trans>Aucun lieu cachère à afficher pour le moment.</Trans>
                        </div>
                    )}

                    {Array.from(placesByCategory.entries()).map(([catId, places]) => {
                        if (places.length === 0) return null;
                        const cat = catId === "__other__" ? null : categoryById.get(catId);
                        const catLocalized = cat ? applyLang(cat, context.lang) : null;
                        return (
                            <section key={catId} className="tr-kosher__section">
                                <h2 className="tr-kosher__section-title">
                                    {catLocalized?.label || <Trans>Autres</Trans>}
                                </h2>
                                <div className="tr-kosher__grid">
                                    {places.map(place => (
                                        <KosherCard
                                            context={context}
                                            key={place.id}
                                            place={place}
                                            category={cat}
                                            lang={context.lang}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
